import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useServiceContent } from '../hooks/use-service-content';
import { useSiteSettings } from '../hooks/use-site-settings';
import { useLanguage } from '../i18n/LanguageContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { services } = useServiceContent();
  const { siteSettings } = useSiteSettings();
  const { tr, localizeText } = useLanguage();

  const serviceTitles = services
    .map((item) => localizeText(item?.title))
    .filter(Boolean)
    .slice(0, 5);

  return (
    <footer className="bg-sage-900 text-sage-100">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-sage-400 to-earth-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">{siteSettings.brandInitials}</span>
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-white text-lg leading-tight">
                  {localizeText(siteSettings.businessName)}
                </span>
                <span className="text-sm text-sage-300">{localizeText(siteSettings.businessTagline)}</span>
              </div>
            </div>
            <p className="text-sage-300 text-sm leading-relaxed">{localizeText(siteSettings.footerDescription)}</p>
          </div>

          <div>
            <h3 className="font-semibold text-white text-lg mb-4">{tr('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sage-300 hover:text-white transition-colors text-sm">
                  {tr('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sage-300 hover:text-white transition-colors text-sm">
                  {tr('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sage-300 hover:text-white transition-colors text-sm">
                  {tr('nav.services')}
                </Link>
              </li>
              <li>
                <Link to="/booking" className="text-sage-300 hover:text-white transition-colors text-sm">
                  {tr('nav.bookAppointment')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sage-300 hover:text-white transition-colors text-sm">
                  {tr('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white text-lg mb-4">{tr('footer.services')}</h3>
            <ul className="space-y-2 text-sm text-sage-300">
              {serviceTitles.map((title) => (
                <li key={title}>{title}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white text-lg mb-4">{tr('footer.contact')}</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href={siteSettings.contactPhoneHref}
                  className="flex items-start gap-2 text-sage-300 hover:text-white transition-colors text-sm group"
                >
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span>{siteSettings.contactPhone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${siteSettings.contactEmail}`}
                  className="flex items-start gap-2 text-sage-300 hover:text-white transition-colors text-sm group"
                >
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span>{siteSettings.contactEmail}</span>
                </a>
              </li>
              <li className="flex items-start gap-2 text-sage-300 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {localizeText(siteSettings.locationLabel)}
                  <br />
                  {localizeText(siteSettings.serviceAreaLabel)}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-sage-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sage-400 text-sm text-center md:text-left">
              © {currentYear} {localizeText(siteSettings.businessName)}. {tr('footer.rightsReserved')}
            </p>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy-policy" className="text-sage-400 hover:text-white transition-colors">
                {tr('legal.privacyPolicy')}
              </Link>
              <Link to="/terms" className="text-sage-400 hover:text-white transition-colors">
                {tr('legal.termsOfService')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
